import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const API_BASE = "https://api.shane9741.workers.dev";

interface ClickRecord {
  id: number;
  timestamp: string;
  ip: string;
  won: boolean;
  winnerKey?: string;
}

interface ClickResponse {
  success: boolean;
  won: boolean;
  ip: string;
  timestamp: string;
  winnerKey?: string;
  message?: string;
  error?: string;
}

interface StatsResponse {
  totalAttempts: number;
  updatedAt: string;
}

// Query Keys
export const queryKeys = {
  winners: ['winners'] as const,
  losers: ['losers'] as const,
  stats: ['stats'] as const,
};

// Fetch functions
const fetchWinners = async (): Promise<ClickRecord[]> => {
  const response = await fetch(`${API_BASE}/winners`);
  if (!response.ok) throw new Error('Failed to fetch winners');
  const data = await response.json();
  return data.winners || [];
};

const fetchLosers = async (): Promise<ClickRecord[]> => {
  const response = await fetch(`${API_BASE}/losers`);
  if (!response.ok) throw new Error('Failed to fetch losers');
  const data = await response.json();
  return data.losers || [];
};

const fetchStats = async (): Promise<StatsResponse> => {
  const response = await fetch(`${API_BASE}/stats`);
  if (!response.ok) throw new Error('Failed to fetch stats');
  return response.json();
};

const submitClick = async (): Promise<ClickResponse> => {
  const response = await fetch(`${API_BASE}/click`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  const data = await response.json();
  return data;
};

// Custom Hooks
export const useWinners = () => {
  return useQuery({
    queryKey: queryKeys.winners,
    queryFn: fetchWinners,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useLosers = () => {
  return useQuery({
    queryKey: queryKeys.losers,
    queryFn: fetchLosers,
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useStats = () => {
  return useQuery({
    queryKey: queryKeys.stats,
    queryFn: fetchStats,
    staleTime: 10 * 1000, // 10 seconds
  });
};

export const useClick = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: submitClick,
    onSuccess: (data) => {
      // Invalidate and refetch stats after every click
      queryClient.invalidateQueries({ queryKey: queryKeys.stats });
      
      // Invalidate appropriate lists based on result
      if (data.won) {
        queryClient.invalidateQueries({ queryKey: queryKeys.winners });
      } else {
        queryClient.invalidateQueries({ queryKey: queryKeys.losers });
      }
    },
  });
};